import { Chip, TableCell, TableRow, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Filters from "../../components/Filters";
import messages from "../../assets/locale/messages";
import Pagination from "../../components/Pagination";
import EmptyState from "../../components/EmptyState";
import SearchInput from "../../components/SearchInput";
import TruncateText from "../../components/TruncateText";
import noImage from "../../assets/images/placeholders/noProductImagePlacholder.svg";
import Table from "../../components/Table";
import noTransactionsPlaceholderImg from "../../assets/images/placeholders/noTransactionsPlaceholder.svg";
import {
  CHIPS_CLASSES,
  CURRENCIES_IDS,
  CURRENCIES_SYMBOLS,
  TRANSACTION_STATUS_IDS
} from "../../utils/Constants";
import { formatDate } from "../../utils/Helpers";
import ListFilter from "../../components/Filters/ListFilter";
import { transactionPaymentReasonsLookupRequest } from "../../store/Lookups/actions";
import { transactionsListRequest } from "../../store/Transactions/actions";

const TransactionsList = () => {
  const lang = useSelector((state) => state.locale.lang);
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.loader.showLoader);
  const { transactions, shared } = messages[lang];

  const transactionsList = useSelector(
    (state) => state.transactions.transactionsList.list
  );
  const transactionsMeta = useSelector(
    (state) => state.transactions.transactionsList.meta
  );
  const transactionPaymentReasonsLookup = useSelector(
    (state) => state.lookups.transactionPaymentReasons.list
  );

  useEffect(() => {
    dispatch(transactionPaymentReasonsLookupRequest());
  }, []);

  const transactionStatusOptions = [
    {
      Id: TRANSACTION_STATUS_IDS.success,
      Name: transactions.transactionsList.filters.succeeded
    },
    {
      Id: TRANSACTION_STATUS_IDS.failure,
      Name: transactions.transactionsList.filters.failed
    }
  ];

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [clearFilters, setClearFilters] = useState(false);
  const [isApplyFilters, setIsApplyFilters] = useState(false);
  const [paymentReasonFilterValue, setPaymentReasonFilterValue] =
    useState([]);
  const [
    transactionStatusFilterValue,
    setTransactionStatusFilterValue
  ] = useState([]);
  const [paramsObject, setParamsObject] = useState({
    payment_reasons: [],
    status: []
  });

  useEffect(() => {
    if (clearFilters) {
      setTransactionStatusFilterValue([]);
      setPaymentReasonFilterValue([]);
      setParamsObject({
        payment_reasons: [],
        status: []
      });
      setClearFilters(false);
    }
  }, [clearFilters]);

  const requestTransactionsList = () => {
    dispatch(
      transactionsListRequest({
        params: {
          page,
          search_value: searchValue,
          ...paramsObject
        }
      })
    );
  };

  useEffect(() => {
    requestTransactionsList();
  }, [page, searchValue]);

  useEffect(() => {
    page > 1 ? setPage(1) : requestTransactionsList();
  }, [paramsObject]);

  useEffect(() => {
    if (isApplyFilters) {
      setParamsObject({
        payment_reasons: paymentReasonFilterValue,
        status: transactionStatusFilterValue
      });
      setIsApplyFilters(false);
    }
  }, [isApplyFilters]);

  const renderFilterContent = () => (
    <div className="filter-content-container h-100">
      <div className="border-bottom py-3 px-md-5 px-3">
        <ListFilter
          list={transactionPaymentReasonsLookup}
          name="payment-reason"
          label={transactions.transactionsList.filters.paymentReason}
          selectedOptionsIds={paymentReasonFilterValue}
          setSelectedOptionsIds={setPaymentReasonFilterValue}
          clearFilters={clearFilters}
          isApplyFilters={isApplyFilters}
          showSearchBar
          searchPlaceholder={
            transactions.transactionsList.filters
              .paymentReasonSearchPlaceholders
          }
          filterModalOpen={filterModalOpen}
        />
      </div>
      <div className="py-3 px-md-5 px-3">
        <ListFilter
          list={transactionStatusOptions}
          name="transaction-status"
          label={transactions.transactionsList.filters.status}
          selectedOptionsIds={transactionStatusFilterValue}
          setSelectedOptionsIds={setTransactionStatusFilterValue}
          clearFilters={clearFilters}
          isApplyFilters={isApplyFilters}
          filterModalOpen={filterModalOpen}
        />
      </div>
    </div>
  );

  const onImageError = (e) => {
    e.target.src = noImage;
  };

  const renderTransactionsTableRow = (
    {
      PaymentReason,
      CompanyName,
      CompanyLogo,
      TransactionId,
      Amount,
      CurrencyId,
      TransactionDate,
      TransactionStatusId,
      TransactionStatusName
    },
    index
  ) => (
    <TableRow key={`request-row-${TransactionId}-${index}`}>
      <TableCell>
        <div className="d-flex align-items-center gap-3">
          <img
            src={CompanyLogo || noImage}
            onError={onImageError}
            alt="companyImage"
            className="logo-img faint-grey-border border-radius-8"
          />
          <TruncateText text={CompanyName || "__"} />
        </div>
      </TableCell>
      <TableCell>{PaymentReason || "__"}</TableCell>
      <TableCell>{TransactionId}</TableCell>
      <TableCell>{`${CurrencyId === CURRENCIES_IDS.usd ? CURRENCIES_SYMBOLS.usd : CURRENCIES_SYMBOLS.euro} ${Amount || 0}`}</TableCell>
      <TableCell>
        {TransactionDate ? formatDate(TransactionDate) : "__"}
      </TableCell>
      <TableCell>
        <Chip
          label={TransactionStatusName}
          className={`${TransactionStatusId === TRANSACTION_STATUS_IDS.success ? CHIPS_CLASSES.green : TransactionStatusId === TRANSACTION_STATUS_IDS.failure ? CHIPS_CLASSES.red : CHIPS_CLASSES.yellow}`}
          sx={{ width: "110px" }}
        />
      </TableCell>
    </TableRow>
  );

  return (
    <div className="transactions-list-container">
      <div className="page-title pb-4 d-flex justify-content-between align-items-center">
        <div className="title-count-container d-flex justify-content-center align-items-center">
          <div>
            <Typography variant="h5" className="title-color">
              {transactions.transactionsList.pageTitle}
            </Typography>
          </div>
          {
            <div className="block-title text-secondary ps-1 ">
              <Typography>{`(${transactionsMeta?.count || "0"} ${transactionsMeta?.count === 1 ? transactions.transactionsList.transaction : transactions.transactionsList.transactions})`}</Typography>
            </div>
          }
        </div>
        <Filters
          handleClearFilters={setClearFilters}
          handleApplyFilters={setIsApplyFilters}
          FilterContent={renderFilterContent()}
          setIsModalOpen={(isOpen) => setFilterModalOpen(isOpen)}
        />
      </div>
      <div className="transactions-table-container bg-white d-flex flex-column w-100  p-4 pb-0 px-0 bg-white border-radius-8">
        <div className="table-header d-flex px-4 align-items-center mb-4">
          <SearchInput
            name="transactionsSearch"
            id="transactionsSearch"
            value={searchValue}
            inputClass="border-radius-8"
            inputWrapperClass="search-input-wrapper search-input-wrapper-width medium-placeholder-search"
            placeholder={
              transactions.transactionsList.searchPlaceholder
            }
            onChange={(value) => {
              setPage(1);
              setSearchValue(value);
            }}
          />
        </div>
        <div className="table-content">
          <div>
            {transactionsList?.length ? (
              <Table
                className={"px-0 nowrap-table"}
                headlines={[
                  transactions.transactionsList.tableHeader
                    .companyName,
                  transactions.transactionsList.tableHeader
                    .paymentReason,
                  transactions.transactionsList.tableHeader
                    .transactionId,
                  transactions.transactionsList.tableHeader
                    .paymentAmount,
                  transactions.transactionsList.tableHeader
                    .transactionDate,
                  shared.lists.status
                ]}
                rows={
                  !isLoading &&
                  transactionsList &&
                  transactionsList.map((transaction, index) =>
                    renderTransactionsTableRow(transaction, index)
                  )
                }
              />
            ) : (
              <div className="border-top">
                {!isLoading && (
                  <EmptyState
                    subTitle={
                      !!searchValue && !transactionsList?.length
                        ? transactions.transactionsList
                            .noSearchResults
                        : transactions.transactionsList
                            .notTransactionsFound
                    }
                    imgSrc={noTransactionsPlaceholderImg}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        <div className="Pagination-container d-flex justify-content-center py-3 mt-2">
          {!isLoading && transactionsList?.length > 0 && (
            <Pagination
              count={transactionsMeta?.pages}
              page={page || transactionsMeta.currentPage}
              handleChange={(page) => {
                setPage(page);
              }}
              defaultPage={1}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionsList;
